"use client";

/**
 * <InstalledAbilityView> — the ability's own page, as an editorial spread.
 *
 * Used by `/abilities/[slug]` for EVERY catalog state (system / personal /
 * installed / available), so an ability looks the same before and after the
 * user installs it. The only state-dependent chrome is the gold Install pill
 * (state='available') and the quiet uninstall foot (installed + unpinned).
 *
 * Replaced the tabbed chrome (Skills · Agents · MCPs · Hooks · Rules ·
 * Interfaces) on 2026-08-09: one scroll, hero → lead → colonnade → chapters,
 * per mockups/ability-internals-editorial-2026-08-08.html (#screen-ability)
 * and docs/superpowers/specs/2026-08-09-ability-internals-editorial-design.md.
 * The Hooks / Rules / Interfaces placeholder tabs went away with it.
 *
 * Data source: the component renders entirely from the `catalogDetail` prop
 * (the parent /abilities/[slug] page already fetches it via
 * `GET /api/abilities/catalog/:slug`). It does NO browser-direct Supabase reads
 * of its own — header meta, skills, and agents all come from `catalogDetail`.
 * Member mutations (add/remove skill or agent) optimistically update local
 * rows, then call `onMembersChanged` so the parent re-fetches and passes a
 * fresh `catalogDetail` back down.
 *
 * Colonnade honesty: a cell is drawn only when its datum exists. There is no
 * `integrations` field on `CatalogAbilityDetail` (only the list row carries
 * one), so the "works with" cell has no source here and is not invented; and
 * because no client path upgrades an installed ability (POST
 * /api/abilities/install 409s on an existing row), a waiting update is stated
 * in amber rather than offered as a button that would do nothing.
 */
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { InstallConfirmModal } from "@/components/abilities/InstallConfirmModal";
import { SkillIcon } from "@/components/SkillsPage/SkillCard";
import { VestraLogoMark } from "@/components/ui/VestraLogoMark";
import {
  removeSkillFromAbility,
  removeAgentFromAbility,
} from "@/lib/api/abilities";
import { cn } from "@/lib/utils";
import { AddToAbilityPicker } from "@/components/abilities/AddToAbilityPicker";
import { GhostRow } from "@/components/routines/GhostRow";
import { AbilityConnections } from "@/components/abilities/AbilityConnections";
import { Button } from "@/components/ui/button";
import Masthead from "@/components/atoms/masthead";
import BackButton from "@/components/atoms/back-button";
import { isAbilitiesSlimV1Enabled } from "@/lib/abilities/flags";
import { useSession } from "@/hooks/useSession";
import {
  type SkillIndexRow as CachedSkillRow,
  type AgentIndexRow as CachedAgentRow,
} from "@/lib/abilities/cache";
import { prefetchSkill, prefetchAgent } from "@/lib/abilities/prefetch";
import type {
  SkillSummary,
  AbilityState,
  CatalogAbilityDetail,
} from "@bash/shared";
import { ExternalLinkIcon } from "@/components/icons/ExternalLinkIcon";

/**
 * Serif-italic taglines for the abilities that have one. Same ones the
 * featured banner on the list carries (AbilitiesTab → FEATURED_SLIDES); kept
 * as a local lookup rather than an import so the detail page doesn't pull the
 * whole marketplace tab into its bundle. An ability with no entry simply has
 * no tagline line — there is no tagline field in the payload to fall back to.
 */
const TAGLINES: Record<string, string> = {
  higgsfield: "your AI creative team",
  "small-business": "your back office, handled",
  sales: "every call prepped, every deal honest",
  "growth-marketing": "plans, ads and outreach that work",
};

function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

type AgentIndexRow = CachedAgentRow;
type SkillIndexRow = CachedSkillRow;

function OpenArrow() {
  return <ExternalLinkIcon />;
}

/** Serif head with the last word italic, the house chapter grammar. The title
 * is an <h2> so the chapters land in the document outline under the hero. */
function SectionHead({ title, count }: { title: string; count?: number }) {
  const cut = title.lastIndexOf(" ");
  const lead = cut === -1 ? "" : title.slice(0, cut + 1);
  const accent = cut === -1 ? title : title.slice(cut + 1);
  return (
    <div className={cn("abd-sect__head", "flex items-baseline gap-3 mb-3.5")}>
      <h2
        className={cn(
          "abd-sect__title",
          "font-serif font-normal text-7 leading-[1.3] tracking-[-0.01em] text-fg-default whitespace-nowrap m-0 [&_em]:italic max-sm:text-6"
        )}
      >
        {lead}
        <em>{accent}</em>
      </h2>
      <span className="flex-1 h-px bg-border-default transform-[translateY(-6px)]" />
      {count !== undefined && (
        <span
          className={cn(
            "abd-sect__count",
            "font-mono text-3 tracking-[0.08em] text-fg-subtle transform-[translateY(-4px)]"
          )}
        >
          {count}
        </span>
      )}
    </div>
  );
}

function Cell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "abd-cell",
        "pt-4 px-6 pb-4.5 border-l last:border-r border-border-subtle max-sm:pt-3 max-sm:px-4 max-sm:pb-3.5"
      )}
    >
      <span
        className={cn(
          "abd-cell__label",
          "font-mono text-2.5 tracking-[0.09em] uppercase text-fg-subtle block mb-1.5"
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "abd-cell__value",
          "inline-flex items-center gap-1.5 text-3.5 text-fg-default [font-feature-settings:'tnum'] [&_img]:w-3.5 [&_img]:h-3.5 [&_img]:rounded-4"
        )}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * One chapter of members. Skills and agents are the same card species — only
 * the route, the prefetch, and the handle sigil differ.
 */
/** Two rows of the 3-col grid; the rest folds behind the house ghost row. */
const GRID_VISIBLE_MAX = 6;

function MemberChapter({
  kind,
  title,
  abilitySlug,
  abilityId,
  members,
  isUserOwned,
  removingSlug,
  onRemove,
  onAdd,
  delay,
  last,
}: {
  kind: "skill" | "agent";
  title: string;
  abilitySlug: string;
  abilityId: string;
  members: SkillSummary[];
  isUserOwned: boolean;
  removingSlug: string | null;
  onRemove: (kind: "skill" | "agent", slug: string) => void;
  onAdd: () => void;
  delay: string;
  /** True when no chapter renders below this one — the collapse gate only
   * exists to keep LATER chapters reachable, so the last chapter renders in
   * full (founder, 08-12). */
  last?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  // Deliberate: with nothing to show and nothing to add, the chapter is left
  // out rather than drawn empty. The colonnade already carries the 0, so the
  // fact is on the page — this only keeps a bare head off it.
  if (members.length === 0 && !isUserOwned) return null;

  const segment = kind === "skill" ? "skills" : "agents";
  const sigil = kind === "skill" ? "/" : "@";
  const warm = kind === "skill" ? prefetchSkill : prefetchAgent;
  const hidden = last ? 0 : members.length - GRID_VISIBLE_MAX;
  const visible =
    expanded || hidden <= 0 ? members : members.slice(0, GRID_VISIBLE_MAX);

  return (
    <section className={cn("abd-sect abd-enter", delay)}>
      <SectionHead title={title} count={members.length} />
      <div
        className={cn(
          "abd-grid",
          "grid grid-cols-3 gap-3 sm:gap-4 max-lg:grid-cols-2 max-sm:grid-cols-[1fr]"
        )}
      >
        {visible.map((member) => (
          <div
            key={member.slug}
            className={cn(
              "abd-card-slot",
              "relative flex min-w-0 hover:[&_.abd-card\_\_remove]:opacity-100 can-hover:[&:focus-within_.abd-card\_\_remove]:opacity-100"
            )}
          >
            <Link
              href={`/abilities/${abilitySlug}/${segment}/${member.slug}`}
              className={cn(
                "abd-card",
                "relative flex-1 min-w-0 text-left font-sans text-[inherit] no-underline bg-bg-elevated border border-border-default rounded-20 pt-6 px-6.5 pb-4.5 cursor-pointer overflow-hidden flex flex-col transition-colors after:[content:''] after:absolute after:-top-22.5 after:-right-22.5 after:w-85 after:h-70 after:bg-gold-glow after:pointer-events-none hover:border-gold-line-34 hover:[&_.abd-card\_\_go]:opacity-100 can-hover:[&:focus-visible_.abd-card\_\_go]:opacity-100 hover:[&_.lib-card__remove]:opacity-100 can-hover:[&:focus-within_.lib-card__remove]:opacity-100"
              )}
              onMouseEnter={() => warm(abilityId, member.slug)}
              onFocus={() => warm(abilityId, member.slug)}
            >
              <span
                className={cn("abd-card__top", "flex items-center gap-3.5")}
              >
                <span
                  className={cn(
                    "abd-card__chip",
                    "w-10 h-10 rounded-12 bg-gold-molten-wash border border-solid border-gold-molten-mid-16 flex items-center justify-center shrink-0"
                  )}
                >
                  <SkillIcon skill={member} size={19} />
                </span>
                <span
                  className={cn(
                    "abd-card__name",
                    "text-4 font-medium tracking-[-0.01em] text-fg-default [overflow-wrap:anywhere]"
                  )}
                >
                  {member.name}
                </span>
              </span>
              {member.description && (
                <span
                  className={cn(
                    "abd-card__desc",
                    "text-3.5 leading-[1.65] text-fg-subtle mt-3.5 flex-1 line-clamp-2 "
                  )}
                >
                  {member.description}
                </span>
              )}
              <span
                className={cn(
                  "abd-card__foot",
                  "flex items-center justify-between gap-2.5 mt-4 pt-3.5 border-t border-border-subtle"
                )}
              >
                <span
                  className={cn(
                    "abd-card__cmd",
                    "font-mono text-3 tracking-[0.02em] text-fg-subtle overflow-hidden text-ellipsis whitespace-nowrap"
                  )}
                >
                  {sigil}
                  {member.slug}
                </span>
                <span
                  className={cn(
                    "abd-card__go",
                    "inline-flex items-center gap-1.5 font-mono text-3 text-gold-ink shrink-0 [&_svg]:w-2.5 [&_svg]:h-2.5 [&_svg]:[stroke:currentColor] can-hover:opacity-0 can-hover:transition-opacity"
                  )}
                >
                  open <OpenArrow />
                </span>
              </span>
            </Link>
            {isUserOwned && (
              <button
                type="button"
                className={cn(
                  "abd-card__remove",
                  "absolute top-3.5 right-3.5 z-[1] font-sans text-3 text-fg-subtle bg-bg-elevated border border-border-default rounded-100 py-1 px-3 cursor-pointer transition-colors hover:text-red hover:border-red-line-40 disabled:opacity-56 disabled:cursor-not-allowed can-hover:opacity-0 can-hover:transition-[opacity,color,border-color] duration-150 ease-out"
                )}
                onClick={() => onRemove(kind, member.slug)}
                disabled={removingSlug === member.slug}
                aria-label={`Remove ${member.name} from this ability`}
              >
                {removingSlug === member.slug ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        ))}
        {isUserOwned && (
          <button
            type="button"
            className={cn(
              "abd-card",
              "abd-card--add",
              "relative flex-1 min-w-0 text-left font-sans text-[inherit] no-underline bg-bg-elevated border border-border-default rounded-20 pt-6 px-6.5 pb-4.5 cursor-pointer overflow-hidden flex flex-col transition-colors after:[content:''] after:absolute after:-top-22.5 after:-right-22.5 after:w-85 after:h-70 after:bg-gold-glow after:pointer-events-none hover:border-gold-line-34 hover:[&_.abd-card\_\_go]:opacity-100 can-hover:[&:focus-visible_.abd-card\_\_go]:opacity-100 hover:[&_.lib-card__remove]:opacity-100 can-hover:[&:focus-within_.lib-card__remove]:opacity-100 items-center justify-center gap-2 flex-row min-h-33 bg-none [border-style:dashed] text-3.5 text-fg-subtle after:content-none hover:text-gold-ink"
            )}
            onClick={onAdd}
          >
            <span aria-hidden>+</span>
            <span>Add {kind}</span>
          </button>
        )}
      </div>
      {hidden > 0 && (
        <GhostRow
          open={expanded}
          label={`Show ${hidden} more ${segment}`}
          openLabel={`Show fewer ${segment}`}
          onClick={() => setExpanded((v) => !v)}
        />
      )}
    </section>
  );
}

export interface InstalledAbilityViewProps {
  /** Ability slug from the route. */
  slug: string;
  /**
   * Catalog state — drives whether to render the Install CTA in the hero.
   * Omit (or pass anything other than 'available') for the default view used
   * by system / personal / installed abilities.
   */
  state?: AbilityState;
  /**
   * Catalog detail payload — the single source of truth for everything this
   * view renders (header meta, skills, agents) plus the install/uninstall
   * modals' required CatalogAbilityDetail shape. The parent (/abilities/[slug]
   * page) already fetches this via GET /api/abilities/catalog/:slug to derive
   * `state`, so the view reads it directly instead of re-fetching.
   */
  catalogDetail: CatalogAbilityDetail;
  /**
   * Called after a member mutation (add/remove skill or agent) so the parent
   * can re-fetch `catalogDetail` and pass fresh skills/agents back down.
   */
  onMembersChanged?: () => void;
  /**
   * Called after a successful install so the parent can flip state from
   * 'available' to 'installed' optimistically.
   */
  onInstalled?: () => void;
  /**
   * Called after a successful uninstall so the parent page can navigate
   * away (typically push the user back to /abilities — the detail page
   * still works but its state semantics change).
   */
  onUninstalled?: () => void;
}

export function InstalledAbilityView({
  slug,
  state,
  catalogDetail,
  onMembersChanged,
  onInstalled,
  onUninstalled,
}: InstalledAbilityViewProps) {
  const router = useRouter();
  const isAvailable = state === "available";
  const isInstalled = state === "installed";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const { session } = useSession();
  // Picker state — single component handles both modes via the `mode` prop.
  const [pickerMode, setPickerMode] = useState<"skill" | "agent" | null>(null);
  // Per-row "removing X..." flag so we can disable the button while the
  // DELETE is in-flight and avoid a double-fire.
  const [removingSlug, setRemovingSlug] = useState<string | null>(null);

  // Seed local skill/agent rows straight from the `catalogDetail` prop — the
  // parent (/abilities/[slug]) already fetched it via GET
  // /api/abilities/catalog/:slug, so there's no browser-direct ability read
  // here anymore. Local state lets us optimistically drop a row on remove;
  // the effect below re-syncs whenever the parent re-fetches (after a mutation
  // it passes a fresh `catalogDetail`).
  const [skillRows, setSkillRows] = useState<SkillIndexRow[]>(
    catalogDetail.skills.map((s) => ({
      ability_id: catalogDetail.id,
      skill_slug: s.slug,
      name: s.name,
      description: s.description,
    }))
  );
  const [agentRows, setAgentRows] = useState<AgentIndexRow[]>(
    catalogDetail.agents.map((a) => ({
      ability_id: catalogDetail.id,
      agent_slug: a.slug,
      name: a.name,
      description: a.description,
    }))
  );
  useEffect(() => {
    setSkillRows(
      catalogDetail.skills.map((s) => ({
        ability_id: catalogDetail.id,
        skill_slug: s.slug,
        name: s.name,
        description: s.description,
      }))
    );
    setAgentRows(
      catalogDetail.agents.map((a) => ({
        ability_id: catalogDetail.id,
        agent_slug: a.slug,
        name: a.name,
        description: a.description,
      }))
    );
  }, [catalogDetail]);

  // Phase 4: a user can compose their own ability if they own it. The catalog
  // endpoint already resolved ownership into `state` — 'personal' means the
  // caller authored it. The edit affordances (remove per card + ghost add tile
  // + picker modal) are gated on this. The server enforces the same check +
  // invariant I1 — the client is just UX.
  const isUserOwned = catalogDetail.state === "personal";
  const isSystem = catalogDetail.state === "system";
  // An ability is a set of skills and connections: with the flag on the agents
  // chapter leaves the page, owner or not, whatever the payload still carries.
  const slim = isAbilitiesSlimV1Enabled();
  // Which chapter is LAST decides where the collapse gate is allowed — the
  // gate exists to keep later chapters reachable, never to gate plain
  // scrolling at the page tail (founder, 08-12). McpList draws itself or
  // nothing, so its verdict arrives at runtime via onCount; until it lands
  // (null) the chapter may still appear, so earlier chapters keep their gate.
  const [mcpCount, setMcpCount] = useState<number | null>(null);
  const mcpMayFollow = mcpCount !== 0;

  const abilitySummary: SkillSummary = {
    slug: catalogDetail.slug,
    name: catalogDetail.name,
    description: catalogDetail.description ?? "",
    category: "general",
    icon: catalogDetail.icon,
    mcps: [],
  };

  async function handleRemove(kind: "skill" | "agent", slug: string) {
    if (removingSlug) return;
    setRemovingSlug(slug);
    try {
      if (kind === "skill")
        await removeSkillFromAbility(catalogDetail.id, slug);
      else await removeAgentFromAbility(catalogDetail.id, slug);
      // Optimistic local update so the card disappears immediately, then the
      // parent re-fetches catalogDetail to reconcile with server state
      // (handles partial failures).
      if (kind === "skill") {
        setSkillRows((rows) => rows.filter((r) => r.skill_slug !== slug));
      } else {
        setAgentRows((rows) => rows.filter((r) => r.agent_slug !== slug));
      }
      onMembersChanged?.();
    } catch (err) {
      // Restore + surface — the optimistic update already happened, but we
      // didn't apply it before the await, so nothing to roll back.
      console.error(`Remove ${kind} failed:`, err);
      alert(`Failed to remove ${kind}: ${(err as Error).message}`);
    } finally {
      setRemovingSlug(null);
    }
  }

  // Build summaries straight from `catalogDetail.skills`/`agents` (they carry
  // name/description/icon). Drive the list off the local `skillRows`/`agentRows`
  // so the optimistic-remove filters still take effect, joining to the prop for
  // the icon. `scope` is derived from the resolved catalog state.
  const summaryScope: "system" | "user" = isSystem ? "system" : "user";
  const skillIconBySlug = new Map(
    catalogDetail.skills.map((s) => [s.slug, s.icon])
  );
  const agentIconBySlug = new Map(
    catalogDetail.agents.map((a) => [a.slug, a.icon])
  );

  const skillSummaries: SkillSummary[] = skillRows.map(
    (row) =>
      ({
        slug: row.skill_slug,
        name: row.name ?? slugToTitle(row.skill_slug),
        description: row.description ?? "",
        category: "general",
        icon: skillIconBySlug.get(row.skill_slug),
        mcps: [],
        scope: summaryScope,
      }) satisfies SkillSummary
  );

  const agentSummaries: SkillSummary[] = agentRows.map(
    (row) =>
      ({
        slug: row.agent_slug,
        name: row.name ?? slugToTitle(row.agent_slug),
        description: row.description ?? "",
        category: "general",
        icon: agentIconBySlug.get(row.agent_slug) ?? "svg:agents",
        mcps: [],
        scope: summaryScope,
      }) satisfies SkillSummary
  );

  // Mirrors MemberChapter's own render guard — an empty chapter only draws
  // for the owner (the ghost add tile keeps it on the page). Under `slim`
  // the chapter is gone, so the skills chapter above it becomes the last one.
  const agentsChapterShown =
    !slim && (agentSummaries.length > 0 || isUserOwned);

  // Tier-2 when_to_use copy — surfaced for any state that has it, as the quiet
  // italic line closing the lead.
  const whenToUse = catalogDetail.when_to_use;
  const tagline = TAGLINES[catalogDetail.slug];

  // The version the user is actually on; the amber note names the one waiting.
  const shownVersion = catalogDetail.installed_version ?? catalogDetail.version;
  const author = catalogDetail.author;
  const hasPublisher = Boolean(author?.name) && author.id !== "unknown";
  // The mark follows the identity, not the state: a Vestra-published ability
  // the user installed is still ours, and every other surface marks it.
  const isVestraMade = isSystem || author?.id === "vestra-official";

  return (
    <div
      className={cn(
        "abd",
        "[--abd-amber:color-mix(in_srgb,_var(--color-amber)_86%,_var(--fg-default))] contents"
      )}
    >
      <header className="flex flex-col gap-y-3">
        <BackButton
          title="Abilities"
          slug={session ? "/library/abilities" : "/abilities"}
          wrapperCl="abd-topbar abd-enter"
        />

        <Masthead
          title={catalogDetail.name}
          subtitle={tagline}
          actions={
            isAvailable ? (
              <Button
                className="abd-btn abd-btn--gold"
                onClick={() => {
                  // Anon can browse the detail but must sign in to install.
                  if (!session) {
                    router.push("/login?next=/abilities/" + catalogDetail.slug);
                    return;
                  }
                  setConfirmOpen(true);
                }}
              >
                Install {catalogDetail.name}
              </Button>
            ) : null
          }
          lead={
            <div
              className={cn(
                "abd-hero__icon",
                "w-17 h-17 rounded-20 bg-gold-molten-wash border border-solid border-gold-molten-mid-18 flex items-center justify-center shrink-0 -mt-1.5 max-sm:w-13 max-sm:h-13 max-sm:rounded-16 max-sm:-mt-2"
              )}
            >
              <SkillIcon skill={abilitySummary} size={32} />
            </div>
          }
          headWrapperClass="abd-hero abd-enter abd-enter--d2"
          mainWrapperClass="abd-hero__main"
          subtitleWrapperClass="abd-hero__tag"
          actionsWrapperClass="abd-hero__side"
        />
      </header>

      {catalogDetail.description && (
        <p
          className={cn(
            "abd-lead",
            "abd-enter",
            "abd-enter--d2",
            "text-4 leading-[1.75] text-fg-muted animate-skill-enter-1 motion-reduce:animate-none [animation-delay:60ms]"
          )}
        >
          {catalogDetail.description}
          {whenToUse && (
            <em
              className={cn(
                "abd-lead__when",
                "block mt-2.5 text-3.5 text-fg-subtle"
              )}
            >
              {whenToUse}
            </em>
          )}
        </p>
      )}

      {/* The colonnade: only what the payload actually knows. */}
      <div
        className={cn(
          "abd-colonnade",
          "abd-enter",
          "abd-enter--d3",
          "overflow-hidden border-y border-border-default animate-skill-enter-1 motion-reduce:animate-none [animation-delay:120ms]"
        )}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 -mx-px">
          <Cell label="Skills">{skillRows.length}</Cell>
          <Cell label="Agents">{agentRows.length}</Cell>
          <Cell label="MCPs">{catalogDetail.mcps.length}</Cell>
          {shownVersion && (
            <Cell label="Version">
              {shownVersion}
              {catalogDetail.update_available && (
                <span className={cn("abd-cell__amber", "text-abd-amber")}>
                  · {catalogDetail.version} ready
                </span>
              )}
            </Cell>
          )}
          {/* Ownership is read first: the studio ability a user composes is
   seeded with a Vestra author in its manifest, and "Made by Vestra"
   would be the wrong answer for the kit they filled themselves. */}
          {isUserOwned ? (
            <Cell label="Made by">
              <span>You</span>
            </Cell>
          ) : isVestraMade ? (
            // alt="": the name follows in text, so a labelled mark would make a
            // screen reader say "Vestra Vestra".
            <Cell label="Made by">
              <VestraLogoMark size={13} alt="" />
              <span>Vestra</span>
            </Cell>
          ) : hasPublisher ? (
            <Cell label="Made by">
              {author.logoUrl && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={author.logoUrl} alt="" aria-hidden />
              )}
              <span>{author.name}</span>
            </Cell>
          ) : null}
        </div>
      </div>

      <MemberChapter
        kind="skill"
        title="The skills"
        abilitySlug={slug}
        abilityId={catalogDetail.id}
        members={skillSummaries}
        isUserOwned={isUserOwned}
        removingSlug={removingSlug}
        onRemove={handleRemove}
        onAdd={() => setPickerMode("skill")}
        delay="abd-enter--d4"
        last={!agentsChapterShown && !mcpMayFollow}
      />

      {!slim && (
        <MemberChapter
          kind="agent"
          title="The agents"
          abilitySlug={slug}
          abilityId={catalogDetail.id}
          members={agentSummaries}
          isUserOwned={isUserOwned}
          removingSlug={removingSlug}
          onRemove={handleRemove}
          onAdd={() => setPickerMode("agent")}
          delay="abd-enter--d5"
          last={!mcpMayFollow}
        />
      )}

      {/* The ABILITY's own bundled MCPs with real per-user status — NOT the
 global My Kit list this chapter used to render (<McpList> reads
 /api/items/mcp/mine with no ability param, so every ability page
 showed the same unrelated connections while the ability's own MCP
 never appeared — founder report, 2026-08-12). The chapter draws
 itself or nothing; onCount feeds the last-chapter computation. */}
      <AbilityConnections
        mcps={catalogDetail.mcps}
        header={<SectionHead title="The connections" />}
        onCount={setMcpCount}
      />

      {isInstalled && !catalogDetail.pinned && (
        <div
          className={cn(
            "abd-foot",
            "abd-enter",
            "abd-enter--d5",
            "pt-5 border-t border-border-subtle flex items-baseline gap-3 flex-wrap animate-skill-enter-1 motion-reduce:animate-none [animation-delay:240ms]"
          )}
        >
          <Button
            variant="danger"
            className="abd-foot__delete"
            onClick={() => setUninstallOpen(true)}
          >
            Uninstall this ability
          </Button>
          <span
            className={cn("abd-foot__hint", "text-3 text-fg-subtle opacity-70")}
          >
            your work and connections stay, only the ability leaves
          </span>
        </div>
      )}

      {isAvailable && (
        <InstallConfirmModal
          open={confirmOpen}
          ability={catalogDetail}
          onCancel={() => setConfirmOpen(false)}
          onSuccess={() => {
            setConfirmOpen(false);
            onInstalled?.();
          }}
        />
      )}

      {isInstalled && (
        <InstallConfirmModal
          uninstall
          open={uninstallOpen}
          ability={catalogDetail}
          onCancel={() => setUninstallOpen(false)}
          onSuccess={() => {
            setUninstallOpen(false);
            onUninstalled?.();
          }}
        />
      )}

      {pickerMode && (
        <AddToAbilityPicker
          mode={pickerMode}
          open={pickerMode !== null}
          abilityId={catalogDetail.id}
          excludeSlugs={
            pickerMode === "skill"
              ? skillRows.map((r) => r.skill_slug)
              : agentRows.map((r) => r.agent_slug)
          }
          onClose={() => setPickerMode(null)}
          onAdded={() => onMembersChanged?.()}
        />
      )}
    </div>
  );
}
